import { TransactionArgument, Transaction, TransactionObjectInput } from '@mysten/sui/transactions'
import {
  normalizeSuiAddress,
  parseStructTag,
  SUI_CLOCK_OBJECT_ID,
  SUI_SYSTEM_STATE_OBJECT_ID,
} from '@mysten/sui/utils'
import { swap as swap_ } from './gen/cetus-integrate/router/functions'
import * as cetusUtil from './gen/kai-leverage-util/cetus/functions'
import * as balance from './gen/sui/balance/functions'
import * as coin from './gen/sui/coin/functions'
import {
  BLUE,
  CETUS,
  CoinInfo,
  stSUI,
  SUI,
  suiUSDT,
  USDC,
  USDY,
  whUSDCe,
  whUSDTe,
  DEEP,
} from '@kunalabs-io/kai'
import { PhantomTypeArgument } from './gen/_framework/reified'
import { compressSuiType } from './gen/_framework/util'
import { ClientWithCoreApi } from '@mysten/sui/client'
import { decodeSignedInt, encodeSignedInt } from '@kunalabs-io/kai'
import { Tick } from './gen/cetus-clmm/tick/structs'
import { Vector } from './gen/_framework/vector'

export const CETUS_GLOBAL_CONFIG_ID =
  '0xdaa46292632c3c4d8f31f23ea0f9b36a28ff3677e9684980e4438403a67a3d8f'
export const CETUS_REWARDER_GLOBAL_VAULT =
  '0xce7bceef26d3ad1f6d9b6f13a953f053e6ed3ca77907516481ce99ae8e588f2b'

export interface PoolInfo {
  coinA: CoinInfo<PhantomTypeArgument>
  coinB: CoinInfo<PhantomTypeArgument>
  poolId: string
}

export interface RedeemInfo {
  coinA: CoinInfo<PhantomTypeArgument>
  coinB: CoinInfo<PhantomTypeArgument>
  protocol: 'stsui'
}

export type SwapInfo = PoolInfo | RedeemInfo

const poolInfos: Array<SwapInfo> = [
  {
    coinA: whUSDTe,
    coinB: whUSDCe,
    poolId: '0xc8d7a1503dc2f9f5b05449a87d8733593e2f0f3e7bffd90541252782e4d2ca20',
  },
  {
    coinA: whUSDCe,
    coinB: SUI,
    poolId: '0xcf994611fd4c48e277ce3ffd4d4364c914af2c3cbb05f7bf6facd371de688630',
  },
  {
    coinA: whUSDTe,
    coinB: SUI,
    poolId: '0x06d8af9e6afd27262db436f0d37b304a041f710c3ea1fa4c3a9bab36b3569ad3',
  },
  {
    coinA: CETUS,
    coinB: SUI,
    poolId: '0x2e041f3fd93646dcc877f783c1f2b7fa62d30271bdef1f21ef002cebf857bded',
  },
  {
    coinA: USDC,
    coinB: whUSDTe,
    poolId: '0x6bd72983b0b5a77774af8c77567bb593b418ae3cd750a5926814fcd236409aaa',
  },
  {
    coinA: USDC,
    coinB: SUI,
    poolId: '0xb8d7d9e66a60c239e7a60110efcf8de6c705580ed924d0dde141f4a0e2c90105',
  },
  {
    coinA: BLUE,
    coinB: SUI,
    poolId: '0x040dc6a93bd4f26ea9507c70f19eb1a060fd5cb9c3718db372f4ae711ffbbb29',
  },
  {
    coinA: USDC,
    coinB: suiUSDT,
    poolId: '0x7df346f8ef98ad20869ff6d2fc7c43c00403a524987509091b39ce61dde00957',
  },
  {
    coinA: stSUI,
    coinB: SUI,
    protocol: 'stsui',
  },
  {
    coinA: USDC,
    coinB: USDY,
    poolId: '0xdcd762ad374686fa890fc4f3b9bbfe2a244e713d7bffbfbd1b9221cb290da2ed',
  },
  {
    coinA: DEEP,
    coinB: SUI,
    poolId: '0xe01243f37f712ef87e556afb9b1d03d0fae13f96d324ec912daffc339dfdcbd2',
  },
]

export interface GetSwapInfoResult {
  swapInfo: SwapInfo
  a2b: boolean
  sqrtPriceLimit: bigint
}

export function getSwapInfo(
  coinIn: CoinInfo<PhantomTypeArgument>,
  coinOut: CoinInfo<PhantomTypeArgument>
): GetSwapInfoResult {
  const swapInfo = poolInfos.find(
    p =>
      (compressSuiType(p.coinA.typeName) === compressSuiType(coinIn.typeName) &&
        compressSuiType(p.coinB.typeName) === compressSuiType(coinOut.typeName)) ||
      (compressSuiType(p.coinA.typeName) === compressSuiType(coinOut.typeName) &&
        compressSuiType(p.coinB.typeName) === compressSuiType(coinIn.typeName))
  )
  if (!swapInfo) {
    throw new Error(`No route found for ${coinIn.typeName} to ${coinOut.typeName}`)
  }

  const a2b = compressSuiType(swapInfo.coinA.typeName) === compressSuiType(coinIn.typeName)
  const sqrtPriceLimit = a2b ? 4295048016n : 79226673515401279992447579055n
  return { swapInfo, a2b, sqrtPriceLimit }
}

export function redeemStSuiCoin(tx: Transaction, coinIn: TransactionObjectInput) {
  const coinOut = tx.moveCall({
    target: `0x059f94b85c07eb74d2847f8255d8cc0a67c9a8dcc039eabf9f8b9e23a0de2700::liquid_staking::redeem`,
    arguments: [
      tx.object('0x1adb343ab351458e151bc392fbf1558b3332467f23bda45ae67cd355a57fd5f5'),
      tx.object(coinIn),
      tx.object(SUI_SYSTEM_STATE_OBJECT_ID),
    ],
    typeArguments: [stSUI.typeName],
  })

  return coinOut
}

export function redeemStSuiBalance(tx: Transaction, balanceIn: TransactionObjectInput) {
  const coinOut = redeemStSuiCoin(tx, coin.fromBalance(tx, stSUI.typeName, balanceIn))
  return coin.intoBalance(tx, SUI.typeName, coinOut)
}

export interface SwapArguments {
  coinIn: TransactionObjectInput
  amount: bigint | TransactionArgument
  byAmountIn: boolean
  coinInInfo: CoinInfo<PhantomTypeArgument>
  coinOutInfo: CoinInfo<PhantomTypeArgument>
}

export function swap(tx: Transaction, args: SwapArguments) {
  const { swapInfo, a2b, sqrtPriceLimit } = getSwapInfo(args.coinInInfo, args.coinOutInfo)

  if ('protocol' in swapInfo) {
    if (swapInfo.protocol !== 'stsui') {
      throw new Error(`protocol ${swapInfo.protocol} not supported`)
    }
    if (!a2b) {
      throw new Error('stSUI minting not supported')
    }

    return {
      coinOut: redeemStSuiCoin(tx, args.coinIn),
      coinInRemaining: coin.zero(tx, swapInfo.coinA.typeName),
    }
  }

  const [outA, outB] = swap_(tx, [swapInfo.coinA.typeName, swapInfo.coinB.typeName], {
    globalConfig: CETUS_GLOBAL_CONFIG_ID,
    pool: swapInfo.poolId,
    coin1: a2b ? args.coinIn : coin.zero(tx, swapInfo.coinA.typeName),
    coin2: a2b ? coin.zero(tx, swapInfo.coinB.typeName) : args.coinIn,
    bool1: a2b,
    bool2: args.byAmountIn,
    u64: args.amount,
    u128: sqrtPriceLimit,
    bool3: false,
    clock: SUI_CLOCK_OBJECT_ID,
  })
  return {
    coinInRemaining: a2b ? outA : outB,
    coinOut: a2b ? outB : outA,
  }
}

export interface FlashSwapArguments {
  amount: bigint | TransactionArgument
  byAmountIn: boolean
  coinInInfo: CoinInfo<PhantomTypeArgument>
  coinOutInfo: CoinInfo<PhantomTypeArgument>
}

export interface FlashSwapReceipt {
  readonly poolInfo: PoolInfo
  readonly a2b: boolean
  readonly object: TransactionObjectInput
}

export function flashSwap(tx: Transaction, args: FlashSwapArguments) {
  const { swapInfo, a2b, sqrtPriceLimit } = getSwapInfo(args.coinInInfo, args.coinOutInfo)

  if ('protocol' in swapInfo) {
    throw new Error('stSUI flash swap not supported')
  }

  const poolInfo = swapInfo as PoolInfo

  const [outA, outB, receipt] = cetusUtil.flashSwap(
    tx,
    [poolInfo.coinA.typeName, poolInfo.coinB.typeName],
    {
      config: CETUS_GLOBAL_CONFIG_ID,
      pool: poolInfo.poolId,
      a2B: a2b,
      byAmountIn: args.byAmountIn,
      amount: args.amount,
      sqrtPriceLimit,
      clock: SUI_CLOCK_OBJECT_ID,
    }
  )
  if (a2b) {
    balance.destroyZero(tx, args.coinInInfo.typeName, outA)
  } else {
    balance.destroyZero(tx, args.coinInInfo.typeName, outB)
  }

  const balanceOut = a2b ? outB : outA

  return {
    balanceOut,
    repayAmount: cetusUtil.swapPayAmount(
      tx,
      [poolInfo.coinA.typeName, poolInfo.coinB.typeName],
      receipt
    ),
    receipt: {
      poolInfo,
      a2b,
      object: receipt,
    } as FlashSwapReceipt,
  }
}

export function repayFlashSwap(
  tx: Transaction,
  repayBalance: TransactionObjectInput,
  receipt: FlashSwapReceipt
) {
  const repayA = receipt.a2b ? repayBalance : balance.zero(tx, receipt.poolInfo.coinA.typeName)
  const repayB = receipt.a2b ? balance.zero(tx, receipt.poolInfo.coinB.typeName) : repayBalance

  cetusUtil.repayFlashSwap(tx, [receipt.poolInfo.coinA.typeName, receipt.poolInfo.coinB.typeName], {
    config: CETUS_GLOBAL_CONFIG_ID,
    pool: receipt.poolInfo.poolId,
    coinA: repayA,
    coinB: repayB,
    receipt: receipt.object,
  })
}

export type FetchParams = {
  client: ClientWithCoreApi
  poolId: string
  coinTypeA: string
  coinTypeB: string
}

type GetTickParams = {
  start?: number
  limit: number
} & FetchParams

export type TickData = {
  index: number
  sqrtPrice: bigint
  liquidityNet: bigint
  liquidityGross: bigint
  feeGrowthOutsideA: bigint
  feeGrowthOutsideB: bigint
  rewardersGrowthOutside: bigint[]
}

export const CETUS_INTEGRATE_PACKAGE_ID =
  '0x6f5e582ede61fe5395b50c4a449ec11479a54d7ff8e0158247adfda60d98970b'

export async function fetchTicks(params: FetchParams): Promise<TickData[]> {
  let ticks: TickData[] = []
  let start: number | undefined = undefined
  const limit = 512

  let hasMore = true
  while (hasMore) {
    const data = await getTicks({
      client: params.client,
      poolId: params.poolId,
      coinTypeA: params.coinTypeA,
      coinTypeB: params.coinTypeB,
      start,
      limit,
    })
    ticks = [...ticks, ...data]
    hasMore = data.length >= limit
    if (hasMore) {
      start = Number(encodeSignedInt(BigInt(data[data.length - 1].index), 32))
    }
  }
  return ticks
}

/**
 * Fetches ticks from the exchange using the simulation exec tx.
 * @param {GetTickParams} params The parameters for the fetch.
 * @returns {Promise<TickData[]>} A promise that resolves to an array of tick data.
 */
export async function getTicks(params: GetTickParams): Promise<TickData[]> {
  const typeArguments = [params.coinTypeA, params.coinTypeB]

  const tx = new Transaction()

  const elements = params.start === undefined ? [] : [tx.pure.u32(params.start)]
  const start = tx.makeMoveVec({
    elements,
    type: 'u32',
  })

  const args = [tx.object(params.poolId), start, tx.pure.u64(params.limit.toString())]

  tx.moveCall({
    target: `${CETUS_INTEGRATE_PACKAGE_ID}::fetcher_script::fetch_ticks`,
    arguments: args,
    typeArguments,
  })

  tx.setSenderIfNotSet(normalizeSuiAddress('0x0'))
  const simulateRes = await params.client.core.simulateTransaction({
    transaction: tx,
    checksEnabled: false,
    include: { events: true, effects: true },
  })

  if (simulateRes.$kind === 'FailedTransaction') {
    const status = simulateRes.FailedTransaction.effects?.status
    const error = status && !status.success ? status.error.message : 'unknown error'
    throw new Error(`getTicks error: ${error}, please check config and tick object ids`)
  }

  const ticks: TickData[] = []

  simulateRes.Transaction.events!.forEach(item => {
    const { name } = parseStructTag(item.eventType)
    if (name !== 'FetchTicksResultEvent') {
      return
    }

    Vector.r(Tick.r)
      .fromBcs(item.bcs)
      .elements.forEach(tick => {
        const tickData: TickData = {
          index: Number(decodeSignedInt(BigInt(tick.index.bits), 32)),
          sqrtPrice: tick.sqrtPrice,
          liquidityNet: decodeSignedInt(tick.liquidityNet.bits, 128),
          liquidityGross: tick.liquidityGross,
          feeGrowthOutsideA: tick.feeGrowthOutsideA,
          feeGrowthOutsideB: tick.feeGrowthOutsideB,
          rewardersGrowthOutside: tick.rewardsGrowthOutside,
        }
        ticks.push(tickData)
      })
  })

  return ticks
}
